import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { packages } from '../../data/packages';
import { agencies } from '../../data/agencies';
import PackageCard from '../../components/PackageCard';
import { useAuth } from '../../lib/auth';
import { useQuery } from '@apollo/client';
import { GET_ALL_PACKAGES, GET_AGENCY_PACKAGES } from '../../graphql/admin/queries/package';
import { GET_AGENCY } from '../../graphql/queries/agency';
import AdminLayout from '../../components/admin/AdminLayout';
// Copie du composant PackageSection depuis AgencyPage
const toISODate = (dateStr: string) => {
  if (!dateStr) return dateStr;
  // Si déjà au format ISO, on garde
  if (dateStr.includes('T')) return dateStr;
  // Sinon, on ajoute T00:00:00.000Z
  return dateStr + 'T00:00:00.000Z';
};

const PackageSection = ({ 
  packages,
  primaryColor 
}: { 
  packages: any[];
  primaryColor: string;
}) => (
  <section id="packages" className="py-20 bg-gray-50">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages
          .filter(pkg => pkg.type === 'B2C')
          .map((pkg) => {
            // Correction du format des dates
            const departures = (pkg.departures || []).map((d: any) => ({
              ...d,
              departureDate: toISODate(d.departureDate),
              returnDate: toISODate(d.returnDate),
            }));
            return (
              <div key={pkg.id} className="flex flex-col">
                <PackageCard 
                  package={{ ...pkg, departures }}
                  primaryColor={primaryColor}
                  isBookingPage={true}
                />
              </div>
            );
          })
        }
      </div>
    </div>
  </section>
);

function normalize(str: string) {
  return str.normalize('NFD').replace(/[ 0-\u036f]/g, '').toLowerCase();
}

export default function NewBookingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userAgencyName = user?.agencyName;
  
  // Récupération de l'ID d'agence utilisateur (robuste)
  const userAgencyId = useMemo(() => {
    // 1. Essayer depuis user.agency.id
    if (user?.agency && typeof user.agency === 'object' && 'id' in user.agency) {
      return user.agency.id;
    }
    // 2. Essayer depuis user.agencyId (direct)
    if (user?.agencyId) {
      return user.agencyId;
    }
    // 3. Fallback: essayer de récupérer depuis localStorage
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed.agency?.id) return parsed.agency.id;
        if (parsed.agencyId) return parsed.agencyId;
      }
    } catch (e) {
      console.error('Error parsing user from localStorage', e);
    }
    return null;
  }, [user]);

  // Récupérer l'ID de l'agence via une query si nécessaire
  const { data: agencyData } = useQuery(GET_AGENCY, {
    variables: { name: userAgencyName },
    skip: !userAgencyName || !!userAgencyId,
  });

  // ID final de l'agence (depuis user ou query)
  const finalAgencyId = useMemo(() => {
    return userAgencyId || agencyData?.agency?.id || null;
  }, [userAgencyId, agencyData]);

  const [filter, setFilter] = useState<'mine' | 'others'>('others');
  const [search, setSearch] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [date, setDate] = useState('');

  // Récupération des packages
  const { data: allData, loading: loadingAll } = useQuery(GET_ALL_PACKAGES);
  const { data: myData, loading: loadingMine } = useQuery(GET_AGENCY_PACKAGES, {
    variables: { agencyName: userAgencyName },
    skip: !userAgencyName,
  });

  const allPackages = allData?.packages || [];
  const myPackages = myData?.agencyPackages || [];
  const loading = filter === 'mine' ? loadingMine : loadingAll;

  // Sélection de la bonne liste selon l'onglet
  // Pour "Offres Partenaires", exclure les packages de l'agence actuelle
  const packagesToShow = useMemo(() => {
    if (filter === 'mine') {
      return myPackages;
    }
    
    // Filtrer les packages pour exclure ceux de l'agence actuelle
    return allPackages.filter((pkg: any) => {
      const pkgAgencyId = pkg.agency?.id || pkg.agencyId;
      const pkgAgencyName = pkg.agency?.name;
      
      // Filtrer par ID si disponible
      if (finalAgencyId && pkgAgencyId) {
        if (pkgAgencyId === finalAgencyId) return false;
      }
      
      // Filtrer par nom d'agence comme fallback
      if (userAgencyName && pkgAgencyName) {
        if (pkgAgencyName === userAgencyName) return false;
      }
      
      return true;
      });
  }, [filter, myPackages, allPackages, finalAgencyId, userAgencyName]);

  // Filtrage des packages selon le filtre sélectionné
  const filteredPackages = packagesToShow.filter((pkg: any) => {
    // Recherche texte sur nom package ou agence
    const agency = agencies.find(a => a.id === (pkg.agency?.id || pkg.agencyId));
    const searchText = normalize(search);
    if (searchText) {
      const matchPackage = normalize(pkg.title).includes(searchText);
      const matchAgency = agency && normalize(agency.name).includes(searchText);
      if (!matchPackage && !matchAgency) return false;
    }
    // Filtre prix (inspiré d'AgencyPage)
    let lowestPrice = Infinity;
    (pkg.formulas || []).forEach((f: any) => {
      let price = 0;
      if (f.priceType === 'PER_PERSON' && typeof f.perPersonPrice === 'number') {
        price = f.perPersonPrice;
      } else if (f.roomPrices) {
        const prices = Object.values(f.roomPrices)
          .map((v) => Number(v))
          .filter((v) => !isNaN(v) && v > 0);
        if (prices.length > 0) price = Math.min(...prices);
      }
      if (price && price < lowestPrice) lowestPrice = price;
    });
    if (minPrice && lowestPrice < parseInt(minPrice)) return false;
    if (maxPrice && lowestPrice > parseInt(maxPrice)) return false;
    // Filtre date
    if (date) {
      const hasDate = (pkg.departures || []).some((d: any) => d.departureDate && d.departureDate.startsWith(date));
      if (!hasDate) return false;
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8 text-center">Choisissez un package à réserver</h1>
        <div className="flex flex-col md:flex-row md:items-end gap-4 mb-8">
          <div className="flex gap-4">
            <button
              className={`px-4 py-2 rounded-lg border ${filter === 'mine' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setFilter('mine')}
            >
              Mes Offres
            </button>
            <button
              className={`px-4 py-2 rounded-lg border ${filter === 'others' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setFilter('others')}
            >
              Offres Partenaires
            </button>
          </div>
          <input
            type="text"
            placeholder="Rechercher par nom de package ou d'agence..."
            className="px-4 py-2 border rounded-lg flex-1"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <input
            type="number"
            placeholder="Prix min"
            className="px-2 py-2 border rounded-lg w-28"
            value={minPrice}
            onChange={e => setMinPrice(e.target.value)}
            min={0}
          />
          <input
            type="number"
            placeholder="Prix max"
            className="px-2 py-2 border rounded-lg w-28"
            value={maxPrice}
            onChange={e => setMaxPrice(e.target.value)}
            min={0}
          />
          <input
            type="date"
            className="px-2 py-2 border rounded-lg w-44"
            value={date}
            onChange={e => setDate(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-500">Chargement des packages...</div>
        ) : (
          <PackageSection packages={filteredPackages} primaryColor="#2563eb" />
        )}
      </div>
    </AdminLayout>
  );
} 